import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["materialsSummary"]

  connect() {
    console.log("Molded candle batch preparation controller connected")

    // Flag to prevent double submission
    this.isSubmitting = false

    // Listen for custom events from child controllers
    this.element.addEventListener('molded-candle-batch-preparation:updateMaterials', () => {
      this.updateMaterials()
    })
  }

  beforeSubmit(event) {
    // Prevent double submission
    if (this.isSubmitting) {
      console.log("Already submitting, skipping...")
      return
    }

    // Prevent default submission
    event.preventDefault()

    console.log("Before submit - expanding checkbox selections into multiple items")

    // Remove any previously auto-generated items
    this.element.querySelectorAll('.auto-generated-item').forEach(item => item.remove())

    // Find all item groups
    const itemFields = this.element.querySelectorAll('.nested-fields:not(.hidden)')
    const itemsContainer = this.element.querySelector('#batch-items')

    // Find the highest existing index in items_attributes
    let maxIndex = 0
    itemFields.forEach(field => {
      const inputs = field.querySelectorAll('input[name*="[molded_candle_variant_id]"]')
      inputs.forEach(input => {
        const match = input.name.match(/\[items_attributes\]\[(\d+)\]/)
        if (match) {
          const index = parseInt(match[1])
          if (index > maxIndex) maxIndex = index
        }
      })
    })

    let nextIndex = maxIndex + 1
    let totalHiddenItems = 0

    itemFields.forEach(field => {
      const checkedCheckboxes = field.querySelectorAll('.mold-variants-checkboxes input[type="checkbox"]:checked')
      console.log(`Found ${checkedCheckboxes.length} checked variants in this field`)

      if (checkedCheckboxes.length === 0) {
        // No variants selected, mark for destruction or remove
        const destroyField = field.querySelector('input[name*="_destroy"]')
        if (destroyField) {
          destroyField.value = '1'
        }
        return
      }

      // Get shared values
      const moldedCandleId = field.querySelector('select[name*="[molded_candle_id]"]')?.value
      const colorVariantId = field.querySelector('select[name*="[molded_candle_color_variant_id]"]')?.value
      const quantity = field.querySelector('input[name*="[quantity]"]')?.value || 1

      console.log(`Molded candle: ${moldedCandleId}, Color variant: ${colorVariantId}, Quantity: ${quantity}`)

      // Set the first variant as the primary one for this field
      const firstCheckbox = checkedCheckboxes[0]
      const hiddenVariantField = field.querySelector('input[name*="[molded_candle_variant_id]"]')
      if (hiddenVariantField) {
        hiddenVariantField.value = firstCheckbox.value
        console.log(`Set primary variant to: ${firstCheckbox.value}`)
      }

      // Create additional hidden items for the remaining checked variants
      for (let i = 1; i < checkedCheckboxes.length; i++) {
        const variantId = checkedCheckboxes[i].value
        console.log(`Creating hidden item for variant: ${variantId} with index: ${nextIndex}`)
        this.createHiddenItem(itemsContainer, moldedCandleId, variantId, colorVariantId, quantity, nextIndex)
        nextIndex++
        totalHiddenItems++
      }
    })

    console.log(`Created ${totalHiddenItems} hidden items. Submitting form...`)

    // Mark as submitting and submit the form
    this.isSubmitting = true
    event.target.submit()
  }

  createHiddenItem(container, moldedCandleId, variantId, colorVariantId, quantity, index) {
    // Create a hidden div with the necessary fields
    const hiddenItem = document.createElement('div')
    hiddenItem.style.display = 'none'
    hiddenItem.className = 'auto-generated-item'

    // Create hidden fields
    const fields = []

    if (moldedCandleId) {
      fields.push(`<input type="hidden" name="crafting_molded_candle_batch_log[items_attributes][${index}][molded_candle_id]" value="${moldedCandleId}">`)
    }

    fields.push(`<input type="hidden" name="crafting_molded_candle_batch_log[items_attributes][${index}][molded_candle_variant_id]" value="${variantId}">`)

    if (colorVariantId) {
      fields.push(`<input type="hidden" name="crafting_molded_candle_batch_log[items_attributes][${index}][molded_candle_color_variant_id]" value="${colorVariantId}">`)
    }

    fields.push(`<input type="hidden" name="crafting_molded_candle_batch_log[items_attributes][${index}][quantity]" value="${quantity}">`)

    hiddenItem.innerHTML = fields.join('')
    container.appendChild(hiddenItem)
  }

  updateMaterials() {
    console.log("Updating materials summary...")

    // Collect all selected variants and quantities
    const items = this.collectItems()

    if (items.length === 0) {
      this.materialsSummaryTarget.innerHTML = '<p class="text-muted">Sélectionnez des variantes pour voir les matériaux nécessaires</p>'
      return
    }

    // Make AJAX request to get materials summary
    const params = new URLSearchParams()
    items.forEach((item, index) => {
      params.append(`items[${index}][variant_id]`, item.variantId)
      params.append(`items[${index}][quantity]`, item.quantity)
      if (item.colorVariantId) {
        params.append(`items[${index}][color_variant_id]`, item.colorVariantId)
      }
    })

    fetch(`/crafting/molded_candle_batch_logs/calculate_materials?${params.toString()}`, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      this.materialsSummaryTarget.innerHTML = html
    })
    .catch(error => {
      console.error('Error fetching materials:', error)
      this.materialsSummaryTarget.innerHTML = '<p class="text-danger">Erreur lors du calcul des matériaux</p>'
    })
  }

  collectItems() {
    const items = []
    const itemFields = this.element.querySelectorAll('.nested-fields:not(.hidden)')

    itemFields.forEach(field => {
      const quantityInput = field.querySelector('input[name*="[quantity]"]')
      const quantity = parseInt(quantityInput?.value) || 1

      const colorVariantSelect = field.querySelector('select[name*="[molded_candle_color_variant_id]"]')
      const colorVariantId = colorVariantSelect?.value || null

      // Collect all checked mold variant checkboxes
      const checkedCheckboxes = field.querySelectorAll('.mold-variants-checkboxes input[type="checkbox"]:checked')

      checkedCheckboxes.forEach(checkbox => {
        items.push({
          variantId: checkbox.value,
          quantity: quantity,
          colorVariantId: colorVariantId
        })
      })
    })

    return items
  }
}

